import clsx from 'clsx'
import React from 'react'

type Props = {
    label?: string
    value: number
    className?: string
    reverse?: boolean // if true, smaller numbers will be green and larger red
}

function RankingScore({
    label,
    value,
    className,
    reverse = false,
}: Props): React.JSX.Element {
    const classes = clsx('', className)

    // Clamp value between 0 and 100
    const clampedVal = Math.max(0, Math.min(value, 100))

    // Normal behavior: Hue goes from 0 (red) to 120 (green)
    // When reverse is true, hue goes from 120 (green) for low values to 0 (red) for high values.
    const hue = reverse
        ? 120 - (120 * clampedVal) / 100
        : (120 * clampedVal) / 100

    // Convert HSL to a CSS color string
    const backgroundColor = `hsl(${hue}, 100%, 50%)`

    // Convert from HSL (with S and L in [0,1]) to RGB [0..255]
    const hslToRgb = (h: number, s: number, l: number) => {
        let r: number, g: number, b: number

        if (s === 0) {
            // Achromatic color (gray)
            r = g = b = l
        } else {
            const hue2rgb = (p: number, q: number, t: number) => {
                if (t < 0) t += 1
                if (t > 1) t -= 1
                if (t < 1 / 6) return p + (q - p) * 6 * t
                if (t < 1 / 2) return q
                if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
                return p
            }

            const q = l < 0.5 ? l * (1 + s) : l + s - l * s
            const p = 2 * l - q

            r = hue2rgb(p, q, h + 1 / 3)
            g = hue2rgb(p, q, h)
            b = hue2rgb(p, q, h - 1 / 3)
        }

        // Convert to 0..255 range
        return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)]
    }

    // Note: hue is in degrees [0..360]. We convert to [0,1] for hslToRgb.
    const [r, g, b] = hslToRgb(hue / 360, 1, 0.5)

    // Calculate perceived luminance (roughly).
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b
    // If it's bright, use black text; otherwise white.
    const textColor = luminance > 150 ? '#000' : '#fff'

    return (
        <div className={classes}>
            {label && <div className="field-label-allow-wrap">{label}</div>}
            <div
                className="font-bold text-center max-w-[80px]"
                style={{
                    backgroundColor,
                    color: textColor,
                }}
            >
                {value}
            </div>
        </div>
    )
}

export default RankingScore
